import {
    IItemListRendererProps,
    IItemModifiers,
    IItemRendererProps,
    IListItemsProps,
} from '@mintdata/mint-kit/impl/properties/components/suggest/common/ListItemsProps';
import {QueryListUtils} from '@mintdata/mint-kit/impl/properties/components/suggest/QueryListUtils';
import {CommonUtils} from '@mintdata/mint-kit/impl/util/CommonUtils';

import * as Keys from '@mintdata/mint-kit/impl/util/Keys';
import * as classnames from 'classnames/bind';

import _isFunction from 'lodash/isFunction';
import * as React from 'react';

const styles = require('@mintdata/mint-kit/impl/properties/components/suggest/QueryList.less');
const cx = classnames.bind(styles);

export interface IQueryListProps<T> extends IListItemsProps<T>
{
    activeItem:T | undefined;
    query:string;
    renderer:(listProps:IQueryListRendererProps<T>) => JSX.Element;
    onActiveItemChange:(activeItem:T | undefined) => void;
    onKeyDown?:React.KeyboardEventHandler<HTMLElement>;
    resetActiveItemToUndefined?:boolean;
    compareItems?:(item1:T, item2:T) => boolean;
}

export interface IQueryListRendererProps<T>
{
    filteredItems:T[];
    handleItemSelect:(item:T, event?:React.SyntheticEvent<HTMLElement>) => void;
    handleKeyDown:React.KeyboardEventHandler<HTMLElement>;
    itemList:React.ReactNode;
}

export interface IQueryListState<T>
{
    filteredItems:T[];
}

export class QueryList<T> extends React.Component<IQueryListProps<T>, IQueryListState<T>>
{
    public static defaultProps = {
        resetActiveItemToUndefined: false,
    };

    public static ofType<T>()
    {
        return QueryList as new (props:IQueryListProps<T>) => QueryList<T>;
    }

    private _itemsParent?:HTMLElement | null;
    private _refHandlers = {
        itemsParent: (ref:HTMLElement | null) => this._itemsParent = ref,
    };

    private _shouldCheckActiveItemInViewport:boolean = false;

    public render()
    {
        const {
            items,
            renderer,
            query,
            itemListRenderer = this._defaultItemListRenderer,
        } = this.props;

        const {filteredItems} = this.state;

        return renderer({
            filteredItems,
            handleItemSelect: this._handleItemSelect,
            handleKeyDown: this._handleKeyDown,
            itemList: itemListRenderer({
                filteredItems,
                items,
                itemsParentRef: this._refHandlers.itemsParent,
                query,
                renderItem: this._renderItem,
            }),
        });
    }

    public componentWillMount()
    {
        this.setState({filteredItems: this._getFilteredItems(this.props)});
    }

    public componentWillReceiveProps(nextProps:IQueryListProps<T>)
    {
        if(
            nextProps.items !== this.props.items ||
            nextProps.itemListPredicate !== this.props.itemListPredicate ||
            nextProps.itemPredicate !== this.props.itemPredicate ||
            nextProps.query !== this.props.query
        )
        {
            this._shouldCheckActiveItemInViewport = true;
            this.setState({filteredItems: this._getFilteredItems(nextProps)});
        }
    }

    public componentDidUpdate()
    {
        const {activeItem, onActiveItemChange, resetActiveItemToUndefined} = this.props;
        const {filteredItems} = this.state;

        if( this._shouldCheckActiveItemInViewport )
        {
            requestAnimationFrame(() => this.scrollActiveItemIntoView());
            this._shouldCheckActiveItemInViewport = false;
        }

        if(
            this._getActiveIndex() < 0 &&
            (filteredItems.length !== 0 || activeItem !== undefined) &&
            !(activeItem === undefined && resetActiveItemToUndefined)
        )
        {
            onActiveItemChange(resetActiveItemToUndefined ? undefined : filteredItems[0]);
        }
    }

    public scrollActiveItemIntoView()
    {
        const {activeItem, onActiveItemChange} = this.props;

        if( activeItem && onActiveItemChange )
            onActiveItemChange(activeItem);
    }

    private _defaultItemListRenderer = (itemListProps:IItemListRendererProps<T>) =>
    {
        const defaultNoResults = (
            <div className={styles.listItem}>
                <div className={styles.listItemText}>No results</div>
            </div>
        );

        const {noResults = defaultNoResults} = this.props;
        const {filteredItems, renderItem, itemsParentRef} = itemListProps;

        const items = filteredItems.map(renderItem).filter(item => item != null);

        return (
            <div ref={itemsParentRef}>{items.length > 0 ? items : noResults}</div>
        );
    };

    private _renderItem = (item:T, index?:number) =>
    {
        const {activeItem, compareItems, query, itemRenderer} = this.props;
        const {filteredItems} = this.state;

        const matchesPredicate = filteredItems.indexOf(item) >= 0;
        const modifiers:IItemModifiers = {
            active: compareItems ? compareItems(activeItem, item) : activeItem === item,
            disabled: false,
            matchesPredicate,
        };

        const itemProps = {
            handleClick: e => this._handleItemSelect(item, e),
            index,
            modifiers,
            query,
        };

        if( _isFunction(itemRenderer) )
        {
            return itemRenderer(item, itemProps);
        }

        return this._defaultItemRenderer(item, itemProps);
    };

    private _defaultItemRenderer = (item:T, itemProps:IItemRendererProps) =>
    {
        const {query, modifiers, handleClick} = itemProps;
        const {active} = modifiers;

        return (
            <div
                className={cx(styles.listItem, {listItemActive: active})}
                key={item.toString()}
                onMouseDown={handleClick}
            >
                <div className={styles.listItemText}>{QueryListUtils.highlightText(item.toString(), query)}</div>
            </div>
        );
    };

    private _defaultItemListPredicate = (query:string, items:T[]) =>
    {
        return items.filter((item) => (
            item.toString().toLowerCase().indexOf(query.toLowerCase()) >= 0
        ));
    };

    private _getFilteredItems(props:IQueryListProps<T>)
    {
        const {
            items,
            itemListPredicate,
            itemPredicate,
            query,
        } = props;

        if( _isFunction(itemListPredicate) )
        {
            return itemListPredicate(query, items);
        }
        else if( _isFunction(itemPredicate) )
        {
            return items.filter((item, index) => itemPredicate(query, item, index));
        }

        return this._defaultItemListPredicate(query, items);
    }

    private _getActiveIndex()
    {
        const {activeItem, compareItems} = this.props;
        const {filteredItems} = this.state;

        if (activeItem == null)
            return -1;

        if( compareItems )
            return filteredItems.findIndex(item => compareItems(item, activeItem));
        else
            return filteredItems.indexOf(activeItem)
    }

    private _moveActiveIndex(direction:number)
    {
        this._shouldCheckActiveItemInViewport = true;

        const {onActiveItemChange} = this.props;
        const {filteredItems} = this.state;
        const maxIndex = Math.max(filteredItems.length - 1, 0);
        const nextActiveIndex = CommonUtils.clamp(this._getActiveIndex() + direction, 0, maxIndex);

        onActiveItemChange(filteredItems[nextActiveIndex]);
    }

    private _handleItemSelect = (item:T, event:React.SyntheticEvent<HTMLElement>) =>
    {
        const {onActiveItemChange, onItemSelect} = this.props;

        onActiveItemChange(item);
        onItemSelect(item, event);
    };

    private _handleKeyDown = (event:React.KeyboardEvent<HTMLElement>) =>
    {
        const {activeItem, onKeyDown} = this.props;

        switch( event.keyCode )
        {
            case Keys.ARROW_UP:
                event.preventDefault();
                this._moveActiveIndex(-1);
                break;
            case Keys.ARROW_DOWN:
                event.preventDefault();
                this._moveActiveIndex(1);
                break;
            case Keys.ENTER:
                if( activeItem != null )
                {
                    event.preventDefault();
                    event.stopPropagation();
                    this._handleItemSelect(activeItem, event);
                }
                break;
            default:
                break;
        }

        if( _isFunction(onKeyDown) )
        {
            onKeyDown(event);
        }
    };
}
